import {
  Button,
  Checkbox,
  Container,
  Flex,
  Form,
  Link,
  List,
  LoadingIcon,
  Message,
  Stack,
  Text,
} from "basis";
import { CriticalErrorMessage } from "components/DebitCardPaymentPage/CriticalErrorMessage";
import { sendMessageToWebview } from "components/DebitCardPaymentPage/sendMessageToWebview";
import { STATIC_RESOURCES } from "constants/contacts";
import Cookie from "js-cookie";
import { Region } from "library/accounts/types";
import { useDebitCardPayment } from "library/hooks/useDebitCardPayment/useDebitCardPayment";
import { formatAmount } from "library/moneyUtils";
import { useEffect, useState } from "react";

const RegionToLegalName = {
  AU: "Latitude Finance Australia",
  NZ: "Gem Finance",
};

export const DebitCardPaymentPage = () => {
  const queryString = window.location.search;
  const params = new URLSearchParams(queryString);
  const accountId = params.get("accountid") || "";
  const supplierBusinessCode = params.get("supplier-business-code") || "";
  const amount = Number(params.get("amount")) / 100;
  const authToken = Cookie.get("auth_token") || "";
  const region = (params.get("region") as Region) || "AU";

  const { submitPayment, error, data, status, quickStreamRef } =
    useDebitCardPayment({
      accountId,
      amount,
      supplierBusinessCode,
      authToken,
      region,
    });

  useEffect(() => {
    if (!data) {
      return;
    }
    const message = JSON.stringify({
      receiptNumber: data.receiptNumber,
      cardNumber: data.cardNumber,
      cardScheme: data.cardScheme,
    });

    sendMessageToWebview(message);
  }, [data]);

  const displayAmount = formatAmount(amount, { withCents: true });

  const onSubmit = ({ values }) => {
    const { saveCardDetails } = values;

    submitPayment({ saveCard: saveCardDetails });
  };

  const [hasCriticalError, setHasCriticalError] = useState(false);

  useEffect(() => {
    if (error?.type === "CRITICAL") {
      setHasCriticalError(true);
    }
  }, [error]);

  const agreementUrl =
    region === "AU"
      ? STATIC_RESOURCES.debitCardServiceAgreementAu
      : STATIC_RESOURCES.debitCardServiceAgreementNz;

  return (
    <Container padding="4">
      <Stack gap="4">
        <Text textStyle="subtitle2" as="h1">
          Pay by debit card
        </Text>
        {hasCriticalError && (
          <CriticalErrorMessage
            onBackClick={() => setHasCriticalError(false)}
          />
        )}
        {!hasCriticalError && (
          <Text textStyle="legal">
            You can pay using your debit card, as long as it has been issued by
            a local bank. Unfortunately, we can’t accept payment from
            international debit cards.
          </Text>
        )}
        {!hasCriticalError && (
          <>
            {status === "LOADING" && (
              <Flex height="200" placeItems="center">
                <LoadingIcon size="medium" />
              </Flex>
            )}
            {status === "ERROR" && error?.type !== "CRITICAL" && (
              <Message role="alert" severity="blocking" bg="secondary.pink.t30">
                {error?.message}
              </Message>
            )}
            {status === "PROCESSING_PAYMENT" && (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  position: "absolute",
                  top: 0,
                  right: 0,
                  zIndex: 1,
                  backgroundColor: "rgba(0,0,0,.7)",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <LoadingIcon color="white" size="medium" />
                  <Text role="alert" color="white">
                    Processing payment...
                  </Text>
                </div>
              </div>
            )}
            <div ref={quickStreamRef} />
            {status !== "LOADING" && (
              <Form
                initialValues={{ saveCardDetails: false }}
                onSubmit={onSubmit}
              >
                <Stack gap="4">
                  <Checkbox
                    name="saveCardDetails"
                    label="Save card details for future payment"
                    hideLabel
                    optional
                  >
                    Save card details for future payment
                  </Checkbox>
                  <Text>
                    By tapping <strong>Pay {displayAmount}</strong>:
                  </Text>
                  <List>
                    <List.Item>
                      <Text textStyle="legal">
                        I authorise and request {RegionToLegalName[region]} to
                        debit my nominated debit card for the payment amount
                        specified above.
                      </Text>
                    </List.Item>
                    <List.Item>
                      <Text textStyle="legal">
                        I agree to the terms and conditions of the{" "}
                        <Link href={agreementUrl} newTab>
                          Debit Card Service Agreement.
                        </Link>
                      </Text>
                    </List.Item>
                  </List>
                  <Button
                    type="submit"
                    testId="submit-payment"
                    width="100%"
                    loading={status === "PROCESSING_PAYMENT"}
                  >
                    Pay {displayAmount}
                  </Button>
                </Stack>
              </Form>
            )}
          </>
        )}
      </Stack>
    </Container>
  );
};
